import { deleteRequest, deleteWithPayload, patch, get, put, post, putMultiPart, postMultiPart } from "../../../shared/data";
import { DEFAULT_LOCALE, CODING_SHOT } from "./helpers";

export const mapBulkUploadTemplate = (payload) => {
    return payload.map(({ shortName, imageViewCode, localeCode }) => {
        return {
            imageViewCode: imageViewCode || shortName,
            localeCode,
            shortName: imageViewCode || shortName,
        };
    });
};

export const mapToSimplePhoto = (photo) => {
    if (!photo) {
        return null;
    }

    return {
        ...photo,
        sortOrder: photo.sortOrder || 9998, // TODO: remove this
        //hasImage: !!photo.uneditedBlobKey,
        imageViewCode: photo.imageViewCode || photo.imageView || photo.shortCode || photo.shortName,
        imageViewDescription: photo.imageViewDescription || photo.name,
        isUploading: false,
        isRotating: false,
        copyInProgress: false,
        localeCode: photo.localeCode || DEFAULT_LOCALE.value,
    };
};

const tempConvert = (view) => {
    return {
        ...view,
        imageViewCode: view.shortName,
        imageViewDescription: view.name,
    };
};

const mapGroups = (group) => {
    return {
        ...group,
        value: group.groupCode,
        text: group.groupDescription,
    };
};

export const mapGs1Codes = (code) => {
    if (!code) {
        return;
    }

    return {
        ...code,
        value: code.id,
        text: code.longDescription,
        isActive: code.isActive,
    };
};

export const getLookups = (includeMultipleImageEligibleItems = true) => {
    //TODO: Remove this when backend changes to imageViewCode
    return get(`/photography/lookups?includeMultipleImageEligibleItems=${includeMultipleImageEligibleItems}`).then((data) => {
        return {
            groups: data.groups.map(mapGroups),
            imageViews: data.imageViews.map(tempConvert),
            packageTypes: data.packageTypes.map((pt) => {
                return {
                    ...pt,
                    imageViews: pt.imageViews.map(tempConvert),
                };
            }),
            editingSources: data.editingSources,
        };
    });
};

export const getNewLookups = () => {
    return get("/photography/package-groups").then((packageGroups) => {
        return packageGroups;
    });
};

export const getRegularImageViews = () => {
    return get(`/photography/image-views`).then((data) => {
        return data.map(tempConvert);
    });
};

export const getGs1Codes = () => {
    return get("/photography/gs1metadata/lookups").then((data) => {
        return {
            imageTypeOptions: data.gs1ImageTypeOptions.map(mapGs1Codes),
            imageFacingOptions: data.gs1ImageFacingOptions.map(mapGs1Codes),
            imageAngleOptions: data.gs1ImageAngleOptions.map(mapGs1Codes),
            imageStateOptions: data.gs1ImageStateOptions.map(mapGs1Codes),
        };
    });
};

export const getGs1CodeForShot = (wipShotKey) => {
    return get(`/photography/gs1metadata/${wipShotKey}`).then((data) => {
        return {
            shortDescription: data.shortDescription,
            imageType: mapGs1Codes(data.gs1ImageType),
            imageFacing: mapGs1Codes(data.gs1ImageFacing),
            imageAngle: mapGs1Codes(data.gs1ImageAngle),
            imageState: mapGs1Codes(data.gs1ImageState),
        };
    });
};

export const saveGs1Code = (wipShotKey, payload) => {
    return put(`/photography/gs1metadata/${wipShotKey}`, payload).then((data) => {
        return {
            shotKey: data.wipShotKey,
            shortDescription: data.shortDescription,
            imageType: data.gs1ImageType,
            imageFacing: data.gs1ImageFacing,
            imageAngle: data.gs1ImageAngle,
            imageState: data.gs1ImageState,
        };
    });
};

export const getEditingSources = (isHighTouch) => {
    return get(`/photography/editing-sources?hasPremiumEditing=${isHighTouch}`).then((data) => {
        return data.map((d) => {
            return {
                text: d.editingSource,
                value: d.editingSourceId,
                isDropdownDefault: d.isDropdownDefault,
            };
        });
    });
};

export const buildPackageTypeOptions = (newPackageTypes, filterBulkUploadTypes = false) => {
    return newPackageTypes
        .map(({ name, packageTypes }) => {
            return {
                label: name,
                options: packageTypes
                    .filter(({ inBulkUpload }) => (filterBulkUploadTypes ? inBulkUpload : !filterBulkUploadTypes))
                    .map(({ code, description }) => {
                        return {
                            text: description,
                            value: code,
                        };
                    }),
            };
        })
        .filter(({ options }) => {
            return options.length;
        });
};

export const buildImageViewSelect = (data, isPremium) => {
    return data.reduce((views, v) => {
        if (v.isVisibleInPicklist && v.isActive && v.isPremiumEditing === isPremium) {
            views.push({ text: v.imageViewDescription, value: v.imageViewCode });
        }

        return views;
    }, []);
};

// Product Shot Template
const mapTemplateIn = (response) => {
    if (!response || !response.shotTemplate) {
        return null;
    }

    return {
        ...response,
        shotTemplate:
            response && response.shotTemplate
                ? response.shotTemplate.map((t) => {
                      return {
                          imageViewCode: t.imageViewCode || t.shortName,
                          localeCode: t.localeCode || DEFAULT_LOCALE.value,
                      };
                  })
                : [],
    };
};

export const mapTemplateOut = (shotsByLocale) => {
    return Object.keys(shotsByLocale).reduce((arr, key) => {
        shotsByLocale[key].forEach((i) => {
            if (!i.imageViewCode || i.imageViewCode === CODING_SHOT.imageViewCode) {
                return;
            }

            arr.push({
                imageViewCode: i.imageViewCode,
                localeCode: i.localeCode,
                shortName: i.imageViewCode,
            });
        }, []);

        return arr;
    }, []);
};

export const getShotTemplate = (wipKey) => {
    return get(`/photography/wips/${wipKey}/template`).then(mapTemplateIn);
};

export const saveShotTemplate = (wipKey, payload, hasShotTemplate) => {
    if (hasShotTemplate) {
        return put(`/photography/wips/${wipKey}/template`, payload, { noRedirect: true });
    } else {
        return post(`/photography/wips/${wipKey}/templates`, payload, { noRedirect: true });
    }
};

export const getSingleShot = (shotKey) => {
    return get(`/photography/wips/shots/${shotKey}`).then(mapToSimplePhoto);
};

//Shots
export const getPhotos = (wipKey, includeMultipleImageEligibleShots = true) => {
    return get(`/photography/wips/${wipKey}/shots?isActive=true&includeMultipleImageEligibleShots=${includeMultipleImageEligibleShots}`).then(
        (photos) => {
            return photos.map((photo) => {
                return mapToSimplePhoto(photo);
            });
        },
    );
};

// Upload Image
export const uploadImage = async (wipKey, taskKey, payload) => {
    return postMultiPart(`/photography/wips/${wipKey}/wip-shots?taskKey=${taskKey}&sourcePage=photography`, payload).then((photo) => {
        if (!photo) {
            return null;
        }

        return mapToSimplePhoto(photo);
    });
};

export const uploadZip = async (wipKey, payload) => {
    return postMultiPart(`/photography/wips/${wipKey}/multiple-image-wip-shots`, payload).then(mapToSimplePhoto);
};

export const updateZip = async (wipKey, shotKey, formData) => {
    return putMultiPart(`/photography/wips/${wipKey}/shots/${shotKey}/multiple-image-shot`, formData).then(mapToSimplePhoto);
};

export const updateImage = async (wipKey, taskKey, shotKey, formData) => {
    // Set update to unedited image
    return putMultiPart(`/photography/wips/${wipKey}/shots/${shotKey}/image?taskKey=${taskKey}&sourcePage=photography`, formData).then((photo) => {
        if (!photo) {
            return null;
        }

        return mapToSimplePhoto(photo);
    });
};

// Swap Shots
export const swapShots = (wipKey, payload) => {
    return post(`/photography/wips/${wipKey}/shots/swap`, payload).then((photos) => {
        if (!photos) {
            return null;
        }

        return photos.map((photo) => {
            return mapToSimplePhoto(photo);
        });
    });
};

export const copyShot = (wipKey, wipShotKey, payload) => {
    return post(`/photography/wips/${wipKey}/duplicate-image/${wipShotKey}`, payload);
};

// Rotate Image
export const rotateImage = (wipKey, shotKey, rotation, imageType) => {
    const payload = {
        rotateDegrees: rotation,
        imageType: imageType,
    };

    return post(`/photography/wips/${wipKey}/shots/${shotKey}/image-rotation`, payload);
};

// Delete Image
export const deleteImage = (wipKey, taskKey, shotKey) => {
    return deleteRequest(`/photography/wips/${wipKey}/shots/${shotKey}/unedited?taskKey=${taskKey}&sourcePage=photography`);
};

// Delete Locale
export const deleteMultipleImages = (wipKey, taskKey, shotKeys) => {
    return deleteWithPayload(`/photography/wips/${wipKey}/wip-shots?taskKey=${taskKey}`, shotKeys);
};

// Skip edit
export const updateShot = (wipKey, taskKey, shotKey, sourcePage, shot) => {
    return patch(`/photography/wips/${wipKey}/shots/${shotKey}?taskKey=${taskKey}&sourcePage=${sourcePage}`, shot).then(mapToSimplePhoto);
};

export const getLocaleOptions = () => {
    return get("/config/locales", { defaultReturn: [] }).then((data) => {
        return data.map((l) => {
            return {
                isActive: l.isActive,
                text: l.name,
                value: l.code,
            };
        });
    });
};

export const rejectProduct = (wipKey, payload) => {
    return post(`/photography/wips/${wipKey}/reject-to-ces`, payload);
};

export const bulkUpload = (payload) => {
    return post("/photography/bulk", payload);
};
